"use client";

import { useState } from "react";

import {
  Box,
  VStack,
  Button,
  IconButton,
  useBreakpointValue,
  Flex,
  Spacer,
  Icon,
  useColorModeValue,
  useToken,
  Avatar,
  Text,
  Link,
} from "@chakra-ui/react";

import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import { useRouter } from "next/router";
import { useUser } from "@/context/UserContext";

import { clearAuthToken } from "../lib/authStorage";
import { motion, AnimatePresence } from "framer-motion";

import {
  FaHome,
  FaUser,
  FaSignOutAlt,
  FaMoneyBillWave,
  FaShoppingCart,
  FaCog,
  FaMoneyCheck,
  FaBuilding,
  FaExchangeAlt,
} from "react-icons/fa";

import { BsPinAngle, BsPinAngleFill } from "react-icons/bs";
import GlassButton from "./pressets/GlassButton";
import { useLayout } from "../context/LayoutContext";
import { useProfile } from "@/context/ProfileContext";
import { clearActiveProfileId } from "@/lib/profileStorage";

export const NAV_EXPANDED_WIDTH = "280px";
export const NAV_COLLAPSED_WIDTH = "100px";

const navItems = [
  {
    items: [
      { label: "Inicio", icon: FaHome, href: "/", type: "all" },
      {
        label: "Mis perfiles",
        icon: FaBuilding,
        href: "/perfiles",
        type: "profiles",
      },
      { label: "Datos personales", icon: FaUser, href: "/datos", type: "all" },

      {
        label: "Ingresos",
        icon: FaMoneyBillWave,
        href: "/ingresos",
        type: "premium",
      },
      {
        label: "Deducciones",
        icon: FaMoneyCheck,
        href: "/deducciones",
        type: "premium",
      },
      {
        label: "Gastos personales",
        icon: FaShoppingCart,
        href: "/gastos",
        type: "all",
      },

      {
        label: "Administración",
        icon: FaCog,
        href: "/admin",
        isAdminOnly: true,
        type: "all",
      },
    ],
  },
];

const NavButton = ({ href, icon, children, onClick, isActive, isExpanded }) => {
  const router = useRouter();

  const activeBg = useColorModeValue("whiteAlpha.300", "whiteAlpha.100");
  const hoverBgToken = useColorModeValue("whiteAlpha.200", "whiteAlpha.50");
  const [resolvedHoverBg] = useToken("colors", [hoverBgToken]);

  return (
    <Button
      as={motion.button}
      whileHover={{ scale: 1.02, backgroundColor: resolvedHoverBg }}
      whileTap={{ scale: 0.98 }}
      href={href}
      onClick={onClick}
      variant="ghost"
      w="full"
      h="48px"
      py={3}
      bg={isActive ? activeBg : "transparent"}
      color="white"
      fontWeight="medium"
      _hover={{ bg: hoverBgToken }}
      transitionProperty="background-color, color"
      transitionDuration="0.2s"
      borderRadius="lg"
      position="relative"
    >
      <Flex
        w="full"
        align="center"
        justify={isExpanded ? "start" : "center"}
        pl={isExpanded ? 4 : 0}
      >
        <Icon as={icon} boxSize="20px" />
        <AnimatePresence>
          {isExpanded && (
            <motion.span
              style={{ whiteSpace: "nowrap" }}
              initial={{ opacity: 0, marginLeft: "0" }}
              animate={{
                opacity: 1,
                marginLeft: "1rem",
                transition: { duration: 0.1, delay: 0.1 },
              }}
              exit={{
                opacity: 0,
                marginLeft: 0,
                transition: { duration: 0.1 },
              }}
            >
              {children}
            </motion.span>
          )}
        </AnimatePresence>
      </Flex>
    </Button>
  );
};

const UserProfile = ({ user, isExpanded }) => {
  return (
    <Flex
      p={isExpanded ? 4 : 2}
      align="center"
      justify={isExpanded ? "start" : "center"}
      w="full"
      h="80px"
      borderBottom="1px solid rgba(255, 255, 255, 0.1)"
      mb={2}
    >
      <Avatar
        size={isExpanded ? "md" : "sm"}
        src={user?.profilePicture}
        name={user?.display_name}
        bg="blue.600"
        color="white"
      />
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            style={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              marginLeft: "1rem",
            }}
            initial={{ opacity: 0, width: 0 }}
            animate={{
              opacity: 1,
              width: "auto",
              transition: { duration: 0.2, delay: 0.15 },
            }}
            exit={{ opacity: 0, width: 0, transition: { duration: 0.15 } }}
          >
            <Text
              color="white"
              fontWeight="semibold"
              maxW="150px"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {user?.display_name || "Usuario"}
            </Text>
          </motion.div>
        )}
      </AnimatePresence>
    </Flex>
  );
};

const NavigationContent = ({ isExpanded, ...props }) => {
  const router = useRouter();
  const { isAdmin, user } = useUser();
  const { activeProfile } = useProfile();

  // Gate modules on the selected profile, not the shared accountType which
  // can stay stale after /perfiles clears the selection.
  const isAccountEnabled =
    !!activeProfile &&
    activeProfile.accountType &&
    activeProfile.accountType !== "none" &&
    !activeProfile.isExpired;
  const buttonPaddingX = 2; // Padding fijo para centrar elementos

  const getVisibility = (item) => {
    if (item.isAdminOnly) return !!isAdmin;
    if (item.type === "profiles") return true;
    if (!isAccountEnabled) return false;
    if (item.type === "premium") {
      return activeProfile?.accountType === "premium";
    }
    return true;
  };

  return (
    <VStack spacing={0} align="stretch" h="full">
      <UserProfile user={user} isExpanded={isExpanded} />
      {activeProfile && (
        <Box px={buttonPaddingX} pb={2}>
          <Button
              variant="ghost"
              w="full"
              h="auto"
              py={2.5}
              color="white"
              bg="whiteAlpha.200"
              border="1px solid"
              borderColor="whiteAlpha.300"
              borderRadius="xl"
              onClick={() => {
                props.handleGoToPage("/perfiles");
                if (props.onClose) props.onClose();
              }}
              _hover={{ bg: "whiteAlpha.300", borderColor: "whiteAlpha.500" }}
              _focusVisible={{ boxShadow: "focus" }}
            >
              <Flex
                w="full"
                align="center"
                justify={isExpanded ? "start" : "center"}
                pl={isExpanded ? 3 : 0}
                gap={2}
              >
                <Flex
                  w="32px"
                  h="32px"
                  borderRadius="lg"
                  bg="purple.400"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Icon as={FaBuilding} boxSize={3.5} />
                </Flex>
                {isExpanded && (
                  <Box textAlign="left" overflow="hidden" flex="1">
                    <Text fontSize="xs" color="whiteAlpha.700">
                      Perfil activo · cambiar
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                      {activeProfile.label || activeProfile.ruc || "Perfil"}
                    </Text>
                    <Text fontSize="xs" color="whiteAlpha.700" noOfLines={1}>
                      {activeProfile.accountType === "premium"
                        ? "Plan completo"
                        : activeProfile.accountType === "basic"
                          ? "Plan básico"
                          : "Sin plan"}
                    </Text>
                  </Box>
                )}
                {isExpanded && <Icon as={FaExchangeAlt} opacity={0.8} />}
              </Flex>
          </Button>
        </Box>
      )}
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        px={buttonPaddingX}
        py={2}
      >
        {navItems.map((group, groupIndex) => (
          <Box key={`group-${groupIndex}`} mb={2}>
            <VStack spacing={1} align="stretch">
              {group.items.map(
                (item) =>
                  getVisibility(item) && (
                    <NavButton
                      key={item.href}
                      href={item.href}
                      icon={item.icon}
                      onClick={(e) => {
                        e.preventDefault();
                        props.handleGoToPage(item.href);
                        if (props.onClose) props.onClose();
                      }}
                      isActive={
                        item.href === "/"
                          ? router.pathname === "/"
                          : router.pathname.startsWith(item.href)
                      }
                      isExpanded={isExpanded}
                    >
                      {item.label}
                    </NavButton>
                  )
              )}
            </VStack>
          </Box>
        ))}
        {props.isMobile && <Spacer />}
      </Box>
      <Flex
        direction="column"
        align="center"
        // Altura fija para el footer de la navegación: 18px (texto) + 8px (gap) + 48px (botón) + 22px (padding top/bottom) = 96px
        h="96px"
        px={buttonPaddingX} // Padding fijo
      >
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{
                opacity: 1,
                height: "18px",
                transition: { duration: 0.2 },
              }}
              exit={{ opacity: 0, height: 0, transition: { duration: 0.1 } }}
              style={{ overflow: "hidden", marginBottom: "8px", width: "100%" }}
            >
              <Text fontSize="xs" color="whiteAlpha.700" textAlign="center">
                Un producto de{" "}
                <Link
                  href="https://codex.com.ec"
                  isExternal
                  fontWeight="bold"
                  color="whiteAlpha.900"
                  _hover={{ color: "blue.300", textDecoration: "underline" }}
                >
                  Codex Ecuador
                </Link>
              </Text>
            </motion.div>
          )}
        </AnimatePresence>
        <GlassButton
          leftIcon={isExpanded ? <Icon as={FaSignOutAlt} /> : undefined}
          colorScheme="red"
          onClick={() => {
            props.handleLogout();
            if (props.onClose) props.onClose();
          }}
          isLoading={props.isLoggingOut}
          loadingText=""
          w="full"
          h="48px"
          py={3}
          fontWeight="medium"
        >
          {isExpanded ? "Cerrar sesión" : <Icon as={FaSignOutAlt} />}
        </GlassButton>
      </Flex>
    </VStack>
  );
};

const Navbar = () => {
  const { isNavPinned, togglePin } = useLayout();
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const router = useRouter();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  const { user } = useUser();

  if (!user) return null;

  const isExpanded = !isMobile && (isNavPinned || isHovered);

  const handleGoToPage = (path) => {
    router.push(path);
  };

  const handleLogout = () => {
    setIsLoggingOut(true);
    setTimeout(() => {
      clearActiveProfileId();
      clearAuthToken();
      router.push("/login");
    }, 1200);
  };

  if (isMobile) {
    const menuVariants = {
      hidden: { x: "-100%", opacity: 0 },
      visible: {
        x: "0%",
        opacity: 1,
        transition: { type: "tween", duration: 0.3 },
      },
      exit: {
        x: "-100%",
        opacity: 0,
        transition: { type: "tween", duration: 0.2 },
      },
    };
    return (
      <>
        <IconButton
          aria-label="Abrir menú"
          icon={<HamburgerIcon boxSize={6} />}
          variant="ghost"
          color="white"
          position="fixed"
          top={3}
          left={3}
          zIndex={1401}
          onClick={() => setIsMobileMenuOpen(true)}
          bg="blackAlpha.500"
          _hover={{ bg: "blackAlpha.700" }}
          borderRadius="md"
          minW="44px"
          minH="44px"
          p={2}
          boxShadow="0 2px 12px rgba(0,0,0,0.35)"
        />
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  zIndex: 1399,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={menuVariants}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "80%",
                  maxWidth: "300px",
                  height: "100vh",
                  zIndex: 1400,
                  backgroundColor: "rgba(18, 23, 32, 0.85)",
                  backdropFilter: "blur(25px) saturate(180%)",
                  borderRight: "1px solid rgba(255, 255, 255, 0.15)",
                  boxShadow: "0 0 40px rgba(0, 0, 0, 0.7)",
                  overflowY: "auto",
                }}
              >
                <NavigationContent
                  handleGoToPage={handleGoToPage}
                  handleLogout={handleLogout}
                  isLoggingOut={isLoggingOut}
                  isExpanded={true}
                  isMobile={true}
                  onClose={() => setIsMobileMenuOpen(false)}
                />
                <IconButton
                  aria-label="Cerrar menú"
                  icon={<CloseIcon />}
                  variant="ghost"
                  color="white"
                  position="absolute"
                  top={4}
                  right={4}
                  zIndex={1402}
                  onClick={() => setIsMobileMenuOpen(false)}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <Box
      as={motion.div}
      initial={false}
      animate={{ width: isExpanded ? NAV_EXPANDED_WIDTH : NAV_COLLAPSED_WIDTH }}
      transition={{ type: "spring", stiffness: 400, damping: 40 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      position="fixed"
      top={0}
      left={0}
      h="100vh"
      zIndex={1100}
      bg="rgba(18, 23, 32, 0.7)"
      backdropFilter="blur(15px) saturate(180%)"
      borderRight="1px solid rgba(255, 255, 255, 0.1)"
      boxShadow="0 0 30px rgba(0, 0, 0, 0.5)"
    >
      <Flex direction="column" h="full" position="relative" overflow="hidden">
        <NavigationContent
          handleGoToPage={handleGoToPage}
          handleLogout={handleLogout}
          isLoggingOut={isLoggingOut}
          isExpanded={isExpanded}
          isMobile={false}
        />
        <Flex
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 0.2 } }}
          p={3}
          justify="center"
          h="56px"
          borderTop="1px solid rgba(255, 255, 255, 0.1)"
        >
          <IconButton
            aria-label="Anclar menú"
            icon={isNavPinned ? <BsPinAngleFill /> : <BsPinAngle />}
            isRound
            size="sm"
            onClick={togglePin}
            bg="whiteAlpha.200"
            color="white"
            _hover={{ bg: "whiteAlpha.300" }}
          />
        </Flex>
      </Flex>
    </Box>
  );
};

export default Navbar;
